import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, Chip, CircularProgress, Alert,
} from '@mui/material';
import { Download } from 'lucide-react';
import { getMonthlyPnL } from '../firebase/monthlyPnLService';
import { useBranch } from '../contexts/BranchContext';
import * as XLSX from 'xlsx';

const fmt = (n) => (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Reports = ({ permissions }) => {
  // Reports is entirely profit/margin analysis — strictly admin-only, never a togglable staff permission.
  const canViewPL = permissions?.userType === 'admin';
  const { activeBranch, loading: branchLoading } = useBranch();
  const [pnlMonth, setPnlMonth] = useState(new Date().toISOString().slice(0, 7));
  const [pnlRecord, setPnlRecord] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);

  useEffect(() => {
    if (!canViewPL || !activeBranch) {
      setPnlRecord(null);
      return;
    }
    setPnlLoading(true);
    getMonthlyPnL(`${activeBranch.id}_${pnlMonth}`).then((res) => {
      setPnlRecord(res.success ? res.data : null);
      setPnlLoading(false);
    });
  }, [pnlMonth, activeBranch, canViewPL]);

  const productLines = pnlRecord?.productLines || [];
  const bestSelling = [...productLines].sort((a, b) => b.lineRevenue - a.lineRevenue).slice(0, 10);
  const byQty = [...productLines].sort((a, b) => b.qtySold - a.qtySold);
  const fastMoving = byQty.slice(0, 5);
  const slowMoving = [...byQty].reverse().slice(0, 5);
  const marginable = productLines.filter((l) => l.lineRevenue > 0);
  const byMargin = [...marginable].sort((a, b) => b.marginPct - a.marginPct);
  const highMargin = byMargin.slice(0, 5);
  const lowMargin = [...byMargin].reverse().slice(0, 5);

  const exportCSV = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  if (!canViewPL) {
    return <Alert severity="warning">You don't have access to Reports.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Reports</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeBranch ? `${activeBranch.name} — ` : ''}Monthly P&L analysis: best sellers, fast/slow movers, margins
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <TextField
              type="month" size="small" label="Month" value={pnlMonth}
              onChange={(e) => setPnlMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button size="small" variant="outlined" startIcon={<Download size={14} />}
              onClick={() => exportCSV(productLines, `Monthly_PnL_${pnlMonth}`)}>CSV</Button>
          </Box>

          {branchLoading || pnlLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : !activeBranch ? (
            <Alert severity="warning">No branch selected. Choose a branch from the header first.</Alert>
          ) : !pnlRecord ? (
            <Alert severity="info">
              No Monthly P&L record has been saved for {activeBranch.name} in {pnlMonth} yet. Fill it in from the Monthly P&L screen.
            </Alert>
          ) : (
            <>
              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                Rankings are based on this month's product-line entries (single-month qty/margin, not day-by-day velocity).
              </Alert>
              {[
                { title: 'Best Selling (by Revenue)', rows: bestSelling, valueKey: 'lineRevenue', valueLabel: 'Revenue' },
                { title: 'Fast Moving (by Qty Sold)', rows: fastMoving, valueKey: 'qtySold', valueLabel: 'Qty Sold' },
                { title: 'Slow Moving (by Qty Sold)', rows: slowMoving, valueKey: 'qtySold', valueLabel: 'Qty Sold' },
                { title: 'High Margin', rows: highMargin, valueKey: 'marginPct', valueLabel: 'Margin %', isPct: true },
                { title: 'Low Margin', rows: lowMargin, valueKey: 'marginPct', valueLabel: 'Margin %', isPct: true },
              ].map((section) => (
                <Box key={section.title} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{section.title}</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Tamil Name</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{section.valueLabel}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {section.rows.map((row, i) => (
                          <TableRow key={row.productId} hover>
                            <TableCell><Chip label={i + 1} size="small" color={i < 3 ? 'primary' : 'default'} /></TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                            <TableCell>{row.nameTa}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {section.isPct ? `${row[section.valueKey].toFixed(1)}%` : fmt(row[section.valueKey])}
                            </TableCell>
                          </TableRow>
                        ))}
                        {section.rows.length === 0 && (
                          <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">No data</Typography>
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Reports;
